import { type ChangeEvent, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { Lightning } from '@phosphor-icons/react';
import { createUniqueName, nameWithoutPostfix } from 'common/deployment/nameAndLabelHelpers';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { produceState } from 'client/state/stateUtils';
import { getDeploymentStageIcon } from 'client/ClientConstants';
import { Catalog } from 'client/components/Catalog';
import { Input } from 'client/components/Input';
import { RadioGroup } from 'client/components/RadioGroup';
import { Stack } from 'client/components/Stack';
import { Color } from 'client/Theme';

const STAGES = [
  {
    identifier: 'development',
    name: 'Development',
    description: (
      <>
        <Lightning weight="bold" /> Dynamic, code editable
      </>
    ),
  },
  {
    identifier: 'staging',
    name: 'Staging',
    description: 'Containerized, images built in GitHub actions',
  },
  {
    identifier: 'production',
    name: 'Production',
    description: 'Containerized, images built in GitHub actions',
  },
];

const stageItems = STAGES.map((stage) => ({
  id: stage.identifier,
  title: stage.name,
  subtitle: stage.description,
  icon: getDeploymentStageIcon(stage.identifier),
}));

interface SettingsGeneralProps {
  showStage?: boolean;
}

export const SettingsGeneral = ({ showStage = false }: SettingsGeneralProps) => {
  const [name, stage, projectName] = useDeploymentClientState(
    (state) => [state.document?.name, state.document?.stage, state.project?.name],
    shallow
  );

  const handleNameChange = produceState(useDeploymentClientState, (state, event: ChangeEvent<HTMLInputElement>) => {
    state.document.name = event.target.value;
  });

  const handleStageSelect = produceState(useDeploymentClientState, (state, stageIdentifier: string) => {
    // If current name is a default name, try to invent a new name based on the stage when the stage
    // changes
    const document = state.document;
    let newName = document.name;

    const oldStageName = STAGES.find((s) => s.identifier == document.stage)!.name;
    const newStageName = STAGES.find((s) => s.identifier == stageIdentifier)!.name;
    const currentName = nameWithoutPostfix(document.name);
    const oldDefaultName = projectName + (oldStageName ? ' ' + oldStageName : '');
    if (currentName == oldDefaultName) {
      const newDefaultName = projectName + (newStageName ? ' ' + newStageName : '');
      newName = createUniqueName({ name: newDefaultName, existing: state.activeDeploymentNames });
    }

    state.document.name = newName;
    state.document.stage = stageIdentifier as 'development' | 'staging' | 'production';
  });

  const stageItem = useMemo(() => {
    const icon = getDeploymentStageIcon(stage || '');
    return {
      id: stage || '',
      title: STAGES.find((s) => s.identifier === stage)?.name || 'Unknown',
      icon: <icon.type color={Color.grey[100]} />,
    };
  }, [stage]);

  return (
    <Stack>
      <Input id="name" label="Name" value={name || ''} large disableAutocomplete onChange={handleNameChange} path="name" />

      {showStage && <RadioGroup label="Stage" items={stageItems} value={stage || null} onChange={(stage) => handleStageSelect(stage)} />}
      {!showStage && <Catalog title="Stage" items={[stageItem]} />}
    </Stack>
  );
};
